/*
  groom.bg cart helpers: the "+" buttons in "Допълни кутията" add one unit through the
  same event flow Horizon's product form uses (CartLinesUpdateEvent + section morph),
  so the drawer, cart page, bubble and box meter all refresh together.
*/
import { CartLinesUpdateEvent, CartErrorEvent, StandardEvents } from '@shopify/events';

const T = window.groomT || {};
const t = (k, fb) => T[k] || fb;

async function addVariant(button) {
  const variantId = Number(button.dataset.groomAdd);
  if (!variantId || button.disabled) return;
  button.disabled = true;
  button.setAttribute('aria-busy', 'true');

  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);

  const deferred = CartLinesUpdateEvent.createPromise();
  button.dispatchEvent(
    new CartLinesUpdateEvent({
      action: 'add',
      context: 'cart',
      lines: [{ merchandiseId: variantId, quantity: 1 }],
      promise: deferred.promise,
    })
  );

  try {
    const response = await fetch(Theme.routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: 1 }], sections: sectionIds.join(',') }),
    }).then((r) => r.json());

    const cart = await fetch(`${Theme.routes.cart_url}.js`).then((r) => r.json());
    if (response.status) {
      button.dispatchEvent(new CartErrorEvent({ error: response.message || 'Add to cart failed', code: 'INVALID' }));
    }
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'groom-box-fillers',
        itemCount: 1,
        sections: response.sections,
        didError: Boolean(response.status),
      },
    });
  } catch (error) {
    deferred.reject(error);
    button.dispatchEvent(new CartErrorEvent({ error: 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' }));
  } finally {
    button.disabled = false;
    button.removeAttribute('aria-busy');
  }
}

document.addEventListener('click', (event) => {
  const button = event.target instanceof Element && event.target.closest('[data-groom-add]');
  if (!button) return;
  event.preventDefault();
  addVariant(button);
});

/* Header mini meter: re-read the cart after any line change (add, quantity, remove). */
// Same shape as the store's Liquid money format ("€22,01").
const money = (cents) => '€' + (cents / 100).toFixed(2).replace('.', ',');

async function refreshMiniMeter() {
  // one in the header (desktop) and one in the category bar (tablet / phone)
  const els = [...document.querySelectorAll('[data-groom-mini]')].filter((el) => el.querySelector('[data-groom-mini-txt]'));
  if (!els.length) return;
  const cart = await fetch(`${Theme.routes.cart_url}.js`).then((r) => r.json());
  els.forEach((el) => {
    const free = Number(el.dataset.free), gift = Number(el.dataset.gift), total = cart.total_price;
    const end = gift > free ? gift : free;
    el.hidden = cart.item_count === 0;
    const txt = el.querySelector('[data-groom-mini-txt]');
    const bar = el.querySelector('[data-groom-mini-bar]');
    if (total < free) txt.innerHTML = `${t('mini_more', 'Още')} <b>${money(free - total)}</b> ${t('mini_to_free', 'до безплатна доставка')}`;
    else if (gift > free && total < gift) txt.innerHTML = `${t('mini_free_and_more', '✓ Безплатна доставка · още')} <b>${money(gift - total)}</b> ${t('mini_to_gift', 'до подарък')}`;
    else txt.textContent = gift > free ? t('mini_free_and_gift', '✓ Безплатна доставка и подарък') : t('mini_free', '✓ Безплатна доставка');
    if (bar) bar.style.width = `${Math.min(100, Math.floor((total * 100) / end))}%`;
  });
}

let miniTimer;
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  clearTimeout(miniTimer);
  const settle = event.promise || event.detail?.promise;
  if (settle && typeof settle.then === 'function') settle.then(refreshMiniMeter, refreshMiniMeter);
  miniTimer = setTimeout(refreshMiniMeter, 1500);
});

/*
  Gift at €80 (theme setting "Show the gift step", window.groomGift). The automatic
  Buy-X-get-Y discount makes one unit free; this keeps the cart in step with it:
  - non-gift subtotal reaches the threshold and no gift line → add the gift
    (the shopper's last pick, else the first cat treat in the cat world, else the first dog treat);
  - subtotal drops below → remove the gift line (otherwise it would be charged);
  - keep exactly one unit; a gift line that isn't a variant of the gift product
    (carts from before the gift product) is swapped for the pick.
  Gift lines carry the hidden property _groom_gift. The gift is one product whose
  variants are the treats (window.groomGift.variants, a = 'dog' | 'cat').
  Since 2026-10-03 the gift variants are priced €0 (owner, after order #1180: TIGER10 typed
  at CHECKOUT, where this code never runs, outranked the BXGY and billed the treat €2,55).
  The €80 threshold is therefore enforced HERE (add at/above, remove below); the BXGY stays
  active but has nothing left to discount. A code no longer removes the gift.
*/
const GIFT_PROP = '_groom_gift';
const GIFT_CHOICE_KEY = 'groom_gift';
let giftBusy = false;

/*
  (Defensive since the €0 prices above — kept for a gift variant that is ever priced again.)
  Shopify applies whichever PRODUCT discount saves the shopper more, so a code like
  TIGER10 can outrank the automatic gift: measured 2026-09-21, a €89,45 cart with the
  code left the gift line at its normal €2,55 and the checkout billed it. A "gift" the
  shopper pays for is worse than no gift, so the line is removed and the cart says why
  (the code is the bigger saving). The reverse case — the gift winning and the code
  going unused — Shopify reports itself via discount_codes[].applicable, and Horizon
  already shows that message.

  outrankedState remembers the cart the gift lost in (codes + non-gift subtotal) so the
  gift is not added, charged, removed and added again in a loop. Any real change to the
  cart or the codes produces a different state and the gift is offered again.
*/
const GIFT_OUTRANKED_KEY = 'groom_gift_outranked';

/* Kept in sessionStorage, not just in memory: otherwise every page view of an outranked
   cart would add the gift, see it charged and remove it again — two cart writes and a
   moment where a line labelled "Подарък" sits there at €2,55. */
let giftOutrankedState = (() => {
  try { return sessionStorage.getItem(GIFT_OUTRANKED_KEY); } catch (e) { return null; }
})();

function rememberGiftOutranked(state) {
  giftOutrankedState = state;
  try {
    if (state) sessionStorage.setItem(GIFT_OUTRANKED_KEY, state);
    else sessionStorage.removeItem(GIFT_OUTRANKED_KEY);
  } catch (e) {
    /* private mode: the in-memory value still holds for this page */
  }
}

const giftCartState = (cart) =>
  JSON.stringify([
    (cart.discount_codes || []).filter((d) => d.applicable !== false).map((d) => d.code).sort(),
    cart.items.filter((i) => !isGift(i)).reduce((sum, i) => sum + i.final_line_price, 0),
  ]);

const markGiftOutranked = (outranked) => {
  document.documentElement.dataset.groomGiftOutranked = outranked ? '1' : '0';
};

const isGift = (item) => Boolean(item.properties && item.properties[GIFT_PROP]);
const cartJson = () => fetch(`${Theme.routes.cart_url}.js`).then((r) => r.json());
const sectionIdList = () =>
  [...document.querySelectorAll('cart-items-component')]
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean)
    .join(',');

async function mutateCart(url, body, action, lines) {
  const deferred = CartLinesUpdateEvent.createPromise();
  document.body.dispatchEvent(new CartLinesUpdateEvent({ action, context: 'cart', lines, promise: deferred.promise }));
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: sectionIdList() }),
    }).then((r) => r.json());
    const cart = await cartJson();
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { items: cart.items, source: 'groom-gift', itemCount: 1, sections: response.sections, didError: Boolean(response.status) },
    });
    return cart;
  } catch (error) {
    deferred.reject(error);
    throw error;
  }
}

const giftVariant = (cfg, id) => (cfg.variants || []).find((v) => v.id === Number(id));

/* The variant to add: the last pick (a variant id), else the first treat for the animal. */
function giftChoice(cfg, animal) {
  let saved = null;
  try { saved = localStorage.getItem(GIFT_CHOICE_KEY); } catch (e) {}
  const pick = giftVariant(cfg, saved);
  if (pick && (!animal || pick.a === animal)) return pick.id;
  const want = animal || (document.documentElement.dataset.world === 'cat' ? 'cat' : 'dog');
  const first = (cfg.variants || []).find((v) => v.a === want) || (cfg.variants || [])[0];
  return first ? first.id : null;
}

function syncGiftButtons(cart, cfg) {
  const line = cart && cart.items.find(isGift);
  const current = giftVariant(cfg, line ? line.variant_id : giftChoice(cfg));
  document.querySelectorAll('[data-groom-gift-animal]').forEach((b) => b.setAttribute('aria-pressed', String(!!current && b.dataset.groomGiftAnimal === current.a)));
}

/* One reconcile pass. Returns 'added' when a gift line was put in the cart, because
   whether that line is free is only known after Shopify has priced it: the caller runs
   a second pass to see the answer. */
async function reconcileGiftPass(cfg) {
  let cart = await cartJson();
  const gifts = cart.items.filter(isGift);
  const base = cart.items.filter((i) => !isGift(i)).reduce((sum, i) => sum + i.final_line_price, 0);
  const qualifies = base >= cfg.at;
  const state = giftCartState(cart);
  const charged = gifts.some((g) => g.final_line_price > 0);

  if (charged) {
    rememberGiftOutranked(state);
    markGiftOutranked(true);
    for (const g of gifts) {
      cart = await mutateCart('/cart/change.js', { id: g.key, quantity: 0 }, 'remove', [{ merchandiseId: g.variant_id, quantity: 0 }]);
    }
    syncGiftButtons(cart, cfg);
    return 'removed';
  }

  if (qualifies && gifts.length === 0 && giftOutrankedState === state) {
    markGiftOutranked(true);
    return 'blocked';
  }

  markGiftOutranked(false);
  rememberGiftOutranked(null);
  let added = false;
  if (qualifies && gifts.length === 1 && !giftVariant(cfg, gifts[0].variant_id)) {
    const id = giftChoice(cfg);
    if (id) {
      cart = await replaceGift(gifts[0], id);
      added = true;
    }
  } else if (qualifies && gifts.length === 0) {
    const id = giftChoice(cfg);
    if (id) {
      cart = await addGiftLine(id);
      added = true;
    }
  } else if (!qualifies && gifts.length > 0) {
    for (const g of gifts) {
      cart = await mutateCart('/cart/change.js', { id: g.key, quantity: 0 }, 'remove', [{ merchandiseId: g.variant_id, quantity: 0 }]);
    }
  } else if (qualifies && (gifts.length > 1 || gifts[0].quantity > 1)) {
    cart = await mutateCart('/cart/change.js', { id: gifts[0].key, quantity: 1 }, 'change', [{ merchandiseId: gifts[0].variant_id, quantity: 1 }]);
    for (const g of gifts.slice(1)) {
      cart = await mutateCart('/cart/change.js', { id: g.key, quantity: 0 }, 'remove', [{ merchandiseId: g.variant_id, quantity: 0 }]);
    }
  }
  syncGiftButtons(cart, cfg);
  return added ? 'added' : 'settled';
}

async function reconcileGift() {
  const cfg = window.groomGift;
  if (!cfg || !cfg.on || giftBusy) return;
  giftBusy = true;
  try {
    // At most two passes: one to put the gift in, one to see the price Shopify gave it.
    for (let pass = 0; pass < 2; pass++) {
      if ((await reconcileGiftPass(cfg)) !== 'added') break;
    }
  } catch (e) {
    console.warn('[groom] gift reconcile failed', e);
  } finally {
    giftBusy = false;
  }
}

/* Sent as 'update', not 'add': the drawer auto-opens on 'add', and a gift the shopper didn't
   click (page load, crossing €80, a flavour swap) shouldn't pop it open. */
const addGiftLine = (id) =>
  mutateCart(Theme.routes.cart_add_url, { items: [{ id, quantity: 1, properties: { [GIFT_PROP]: '1' } }] }, 'update', [{ merchandiseId: id, quantity: 1 }]);

async function replaceGift(line, id) {
  await mutateCart('/cart/change.js', { id: line.key, quantity: 0 }, 'remove', [{ merchandiseId: line.variant_id, quantity: 0 }]);
  return addGiftLine(id);
}

/* Swap the gift line to variant id (the flavour list, or an animal button's pick). */
async function switchGift(id) {
  const cfg = window.groomGift;
  if (!cfg || !cfg.on || !giftVariant(cfg, id) || giftBusy) return;
  try { localStorage.setItem(GIFT_CHOICE_KEY, String(id)); } catch (e) {}
  giftBusy = true;
  try {
    let cart = await cartJson();
    const line = cart.items.find(isGift);
    if (line && line.variant_id !== Number(id)) cart = await replaceGift(line, Number(id));
    syncGiftButtons(cart, cfg);
  } finally {
    giftBusy = false;
  }
}

document.addEventListener('click', (event) => {
  const btn = event.target instanceof Element && event.target.closest('[data-groom-gift-animal]');
  if (!btn || !window.groomGift) return;
  event.preventDefault();
  const id = giftChoice(window.groomGift, btn.dataset.groomGiftAnimal);
  if (id) switchGift(id);
});
document.addEventListener('change', (event) => {
  const select = event.target instanceof Element && event.target.closest('[data-groom-gift-select]');
  if (select instanceof HTMLSelectElement) switchGift(select.value);
});

/*
  Speedy office picker assets. layout/theme.liquid ships them only when the cart already
  has lines (or on the cart template); on an empty cart the picker cannot render, because
  its markup is gated on `cart != empty`. When an empty cart gains its first line, Horizon
  morphs the cart summary in and <groom-speedy-picker> needs its definition — inject them
  here. Fetching starts alongside the cart request rather than after the morph, and a
  custom element upgrades whenever its definition arrives, so there is no ordering
  requirement. When theme.liquid already loaded them, groomSpeedyAssets is undefined and
  this is a no-op.
*/
function loadSpeedyAssets() {
  const urls = window.groomSpeedyAssets;
  if (!urls) return;
  delete window.groomSpeedyAssets;
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = urls.css;
  document.head.appendChild(link);
  const script = document.createElement('script');
  script.src = urls.js;
  script.defer = true;
  document.head.appendChild(script);
}

let giftTimer;
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  loadSpeedyAssets();
  if (giftBusy) return;
  clearTimeout(giftTimer);
  const settle = event.promise || event.detail?.promise;
  const run = () => { clearTimeout(giftTimer); giftTimer = setTimeout(reconcileGift, 150); };
  if (settle && typeof settle.then === 'function') settle.then(run, run);
  else giftTimer = setTimeout(reconcileGift, 1500);
});
/* Applying or removing a code changes which product discount wins, so the gift has to be
   reconsidered even though no cart line moved. */
document.addEventListener(StandardEvents.cartDiscountUpdate, () => {
  if (giftBusy) return;
  clearTimeout(giftTimer);
  giftTimer = setTimeout(reconcileGift, 150);
});

/*
  Startup reconcile. With the gift flow on, this used to issue an uncached GET /cart.js on
  every single page view just to decide whether a gift line needed adding or removing.

  An empty cart has no gift line and cannot be over the threshold, so there is nothing to
  decide and the round trip is skipped. The guard belongs here and nowhere else: reconcileGift
  is also the cartLinesUpdate handler above, and groomGift.items is a page-load snapshot that
  nothing updates — testing it inside reconcileGift would mean a cart filled over AJAX on a
  page that loaded empty never gets its gift.
*/
if (window.groomGift && window.groomGift.on && window.groomGift.items > 0) {
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', reconcileGift, { once: true });
  else reconcileGift();
}
